import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { COMPANY_INFO, BUSINESS_HOURS, FAQS, SERVICE_LABELS } from '../constants';
import getEmailParams from '../templates/email';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    description: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Auto-hide success modal after 5 seconds
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // EmailJS configuration
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

      const emailParams = getEmailParams(formData);

      // Send email using EmailJS
      await emailjs.send(
        serviceId,
        templateId,
        emailParams,
        publicKey
      );

      setSubmitStatus('success');
      setShowSuccessModal(true);

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        description: ''
      });

    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✓</div>
            <h3>Thank You!</h3>
            <p>We got your message!</p>
            <p className="success-detail">
              We'll get back to you within 2 business days at <strong>{formData.email || 'your email'}</strong>
            </p>
            <button
              className="btn"
              onClick={() => setShowSuccessModal(false)}
              style={{ marginTop: '1rem' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero" style={{ padding: '150px 2rem 80px' }}>
        <h1>Get in Touch</h1>
        <p>We'd love to hear from you. Let's start a conversation.</p>
      </section>

      {/* Contact Section */}
      <section>
        <div className="container">
          <div className="grid grid-2" style={{ gap: '4rem' }}>
            {/* Contact Form */}
            <div>
              <br />
              <p style={{ marginBottom: '2rem' }}>
                Fill out the form and we'll get back to you within 2 business days.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Your Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Smith"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@company.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company Name</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company Inc"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="service">What Do You Need Help With? *</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a service</option>
                    {Object.entries(SERVICE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Briefly describe your needs or questions (max 200 characters)"
                    maxLength={200}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="btn btn-large"
                  style={{ width: '100%' }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'error' && (
                  <div className="error-banner">
                    <p>
                      ⚠️ Sorry, there was an error sending your message.
                      Please try again or email us directly at{' '}
                      <a href="mailto:info.topfluxe@gmail.com">info.topfluxe@gmail.com</a>
                    </p>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2>Contact Information</h2>

              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3>📧 Email</h3>
                <p style={{ fontSize: '1.1rem' }}>
                  <a href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</a>
                </p>
                <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem', fontSize: '0.95rem' }}>
                  {BUSINESS_HOURS.response}
                </p>
              </div>

              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3>📞 Phone</h3>
                <p style={{ fontSize: '1.1rem' }}>
                  <a href={`tel:${COMPANY_INFO.phone.replace(/[^0-9+]/g, '')}`}>
                    {COMPANY_INFO.phone}
                  </a>
                </p>
                <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem', fontSize: '0.95rem' }}>
                  {BUSINESS_HOURS.weekdays}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ backgroundColor: 'var(--bg-light)' }}>
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Quick answers before you reach out</p>
          </div>
          {FAQS.map(faq => (
            <div key={faq.id} className="card" style={{ marginBottom: '1.5rem' }}>
              <h4>{faq.question}</h4>
              <p style={{ margin: 0 }}>{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default Contact;